import { AutocompleteSelect } from '@/components/ui/AutocompleteSelect'
import { Button } from '@/components/ui/Button'
import { Fieldset } from '@/components/ui/Fieldset'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { geolocateApi } from '@/features/geolocate/api/geolocateApi'
import { toastService } from '@/lib/toastService'
import { HStack, Stack } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { districtsApi, districtsBigApi } from '../api/streetsApi'

/**
 * District Modal Component
 * Form for creating/editing micro-districts
 */
export function DistrictModal({ isOpen, onClose, district }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isEdit = !!district
  const [selectedCity, setSelectedCity] = useState(null)

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      district: '',
      city: null,
      district_big: null,
    },
  })

  const cityValue = watch('city')

  // Reset district_big when city changes
  useEffect(() => {
    if (cityValue && cityValue.id !== selectedCity?.id) {
      setValue('district_big', null)
      setSelectedCity(cityValue)
    }
  }, [cityValue, selectedCity, setValue])

  // Populate form when editing
  useEffect(() => {
    if (district) {
      const cityObj = district.city ? { id: district.city_id, name: district.city_name } : null
      const districtBigObj = district.district_big ? { id: district.district_big_id, district_big: district.district_big_name } : null

      reset({
        district: district.district || '',
        city: cityObj,
        district_big: districtBigObj,
      })
      setSelectedCity(cityObj)
    } else {
      reset({ district: '', city: null, district_big: null })
      setSelectedCity(null)
    }
  }, [district, reset])

  // Fetch districts_big based on selected city
  const fetchDistrictsBig = async ({ search, page, page_size }) => {
    if (!cityValue) return { data: [] }
    const params = { search, page, page_size }
    if (cityValue?.id) params.city = cityValue.id
    return districtsBigApi.getAll(params)
  }

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (formData) => {
      const data = {
        district: formData.district,
        city: formData.city?.id,
        district_big: formData.district_big?.id,
      }
      return isEdit ? districtsApi.update(district.id, data) : districtsApi.create(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['districts'] })
      toastService.success({
        title: t('common.success'),
        description: isEdit ? t('streets.districtUpdated') : t('streets.districtCreated'),
      })
      onClose()
    },
    onError: (error) => {
      toastService.error({
        title: t('common.error'),
        description: error?.message || t('streets.saveError'),
      })
    },
  })

  const onSubmit = (data) => {
    saveMutation.mutate(data)
  }

  const handleClose = () => {
    if (!saveMutation.isPending) {
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? t('streets.editDistrict') : t('streets.createDistrict')}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4}>
          <Fieldset>
            <Fieldset.Content>
              <Stack gap={4}>
                <Controller
                  name="district"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field }) => (
                    <Input
                      label={`${t('streets.district')} *`}
                      error={errors.district?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="city"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field }) => (
                    <AutocompleteSelect
                      label={`${t('streets.city')} *`}
                      placeholder={t('streets.selectCity')}
                      fetchOptions={(search) => geolocateApi.getCitiesAutocomplete({ search })}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                      error={errors.city?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="district_big"
                  control={control}
                  render={({ field }) => (
                    <AutocompleteSelect
                      label={t('streets.districtBig')}
                      placeholder={t('streets.selectDistrictBig')}
                      fetchOptions={fetchDistrictsBig}
                      getOptionLabel={(option) => option.district_big}
                      getOptionValue={(option) => option.id}
                      error={errors.district_big?.message}
                      isDisabled={!cityValue}
                      {...field}
                    />
                  )}
                />
              </Stack>
            </Fieldset.Content>
          </Fieldset>

          {/* Actions */}
          <HStack justify="flex-end" spacing={3} pt={2}>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={handleClose}
              disabled={saveMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="md"
              loading={saveMutation.isPending}
            >
              {t('common.save')}
            </Button>
          </HStack>
        </Stack>
      </form>
    </Modal>
  )
}
